import React, { useCallback, useEffect } from 'react';
import { Drawer, IconButton, Box, Tooltip } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import { useAnda } from './AndaContext';
import AndaHistoryMenu from './AndaHistoryMenu';
import AndaMessageList from './AndaMessageList';
import AndaChatInput from './AndaChatInput';
import { getChatHistory } from '../../api/andaApi';
import './anda.scss';

export default function AndaChatPanel() {
  const { chatOpen, historyOpen, chatWidth, setChatWidth, setResizing, closeChat, toggleHistory, startNewChat, setHistoryList, isStreaming, MIN_CHAT_WIDTH, MAX_CHAT_WIDTH_RATIO } = useAnda();

  const projectId = Number(localStorage.getItem('project_id')) || null;
  const acctNo = Number(localStorage.getItem('user_id')) || null;

  useEffect(() => {
    if ((chatOpen || historyOpen) && projectId && acctNo) {
      getChatHistory({ acct_no: acctNo, project_id: projectId })
        .then(setHistoryList)
        .catch(() => setHistoryList([]));
    }
  }, [chatOpen, historyOpen, projectId, acctNo, setHistoryList]);

  const handleResize = useCallback(
    (e) => {
      const maxW = window.innerWidth * MAX_CHAT_WIDTH_RATIO;
      const newWidth = Math.round(Math.min(maxW, Math.max(MIN_CHAT_WIDTH, window.innerWidth - e.clientX)));
      setChatWidth(newWidth);
    },
    [setChatWidth, MIN_CHAT_WIDTH, MAX_CHAT_WIDTH_RATIO]
  );

  const handleResizeStart = useCallback(
    (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      setResizing(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      const onMouseMove = (ev) => handleResize(ev);
      const onMouseUp = () => {
        setResizing(false);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [handleResize, setResizing]
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={chatOpen}
        onClose={closeChat}
        variant="persistent"
        ModalProps={{ hideBackdrop: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: chatWidth,
            maxWidth: '100vw',
            boxSizing: 'border-box',
            top: 50,
            height: 'calc(100% - 50px)',
          },
        }}
        className="anda-drawer"
      >
        <Box className="anda-panel" sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          <div role="separator" className="anda-resize-handle" aria-label="調整寬度" onMouseDown={handleResizeStart} />
          <header className="anda-header">
            <Tooltip title="歷史對話" placement="bottom">
              <IconButton size="small" onClick={toggleHistory} aria-label="歷史對話" sx={{ color: '#323232' }}>
                <MenuIcon />
              </IconButton>
            </Tooltip>
            <span className="anda-title">安答 Anda</span>
            <div className="anda-header-actions">
              <Tooltip title={isStreaming ? '請等待回覆完成後再開始新對話' : '開始新的對話'} placement="bottom">
                <IconButton size="small" onClick={startNewChat} disabled={isStreaming} aria-label="新對話" sx={{ color: '#323232' }}>
                  <EditIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <IconButton size="small" aria-label="更多" sx={{ color: '#323232' }}>
                <MoreVertIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton size="small" onClick={closeChat} aria-label="關閉" sx={{ color: '#323232' }}>
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </div>
          </header>
          <AndaHistoryMenu />
          <div className="anda-body">
            <AndaMessageList />
            <AndaChatInput />
          </div>
        </Box>
      </Drawer>
    </>
  );
}
